import React, { useState, useEffect, useContext } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { Play, RefreshCw, ChevronDown, BarChart2, Bug, ArrowRight } from 'lucide-react';
import { toast } from 'react-toastify';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useBatches, useEvalResults, useRunEvaluation, useEvalResult, useEvalResultDetail, useHealth } from '../../../hooks/console/useConsoleAPI';
import Pagination from '../shared/Pagination';
import { CONSOLE_VERDICT_STYLE, CONSOLE_CATEGORIES, getVBg, getVText, getScoreColor } from '../../../constants/consoleConstants';
import { AuthContext } from '../../../../../../context/AuthContext';

const MAX_VISIBLE_BATCHES = 5;

const PER = 10;

const INIT_RUN = { batch_name: '', engine_version: '', llm_model: '', run_mode: 'ALL', qa_ids: '', category: '', created_by: '' };

const BATCH_STATUS_LABEL = { COMPLETED: '완료', RUNNING: '실행 중', PENDING: '대기 중', FAILED: '실패' };

function makeDefaultBatchName() {
    const d = new Date();
    const ymd = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return `${ymd} 평가`;
}

export default function EvaluationTab() {
    const { auth } = useContext(AuthContext);
    const navigate = useNavigate();
    const { data: healthData } = useHealth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [selectedBatchId, setSelectedBatchId] = useState(searchParams.get('batch_id') ?? '');
    const [showAllBatches, setShowAllBatches] = useState(false);

    // result_id로 진입한 경우: 해당 결과의 batch_id를 조회해서 배치 자동 선택
    const initResultId = searchParams.get('result_id') ?? null;
    const { data: initResultData } = useEvalResult(initResultId);
    useEffect(() => {
        if (initResultData?.batch_id) {
            setSelectedBatchId(String(initResultData.batch_id));
            setSearchParams({}, { replace: true });
        }
    }, [initResultData]);

    // URL batch_id / keyword 파라미터 소비
    useEffect(() => {
        const urlBatchId = searchParams.get('batch_id');
        const urlKeyword  = searchParams.get('keyword');
        if (urlBatchId || urlKeyword) {
            if (urlKeyword) setFilterKeyword(urlKeyword);
            setSearchParams({}, { replace: true });
        }
    }, []);
    const [filterGrade, setFilterGrade] = useState('all');
    const [filterCategory, setFilterCategory] = useState('');
    const [filterKeyword, setFilterKeyword] = useState('');
    const [selectedId, setSelectedId] = useState(null);
    const [page, setPage] = useState(1);
    const [showRunModal, setShowRunModal] = useState(false);
    const [runForm, setRunForm] = useState({ ...INIT_RUN });
    const [debugId, setDebugId] = useState(null);

    const { data: batchData, isLoading: bLoading } = useBatches();
    const batches = batchData?.items ?? [];

    const resultParams = {
        ...(selectedBatchId ? { batch_id: selectedBatchId } : {}),
        ...(filterGrade !== 'all' ? { grade: filterGrade } : {}),
        ...(filterCategory ? { category: filterCategory } : {}),
        ...(filterKeyword ? { keyword: filterKeyword } : {}),
        page, size: PER,
    };
    const { data: resultData, isLoading: rLoading } = useEvalResults(resultParams);
    const results = resultData?.items ?? [];
    const total = resultData?.total ?? 0;
    const totalPages = Math.ceil(total / PER);

    const runMutation = useRunEvaluation();
    const { data: selectedDetail, isLoading: detailLoading } = useEvalResult(selectedId);
    const { data: debugData, isLoading: debugLoading } = useEvalResultDetail(debugId);

    const selectedBatch = batches.find((b) => String(b.id) === String(selectedBatchId));
    const visibleBatches = showAllBatches ? batches : batches.slice(0, MAX_VISIBLE_BATCHES);

    const gradeCount = (g) => {
        if (!selectedBatch) return null;
        if (g === 'PASS') return selectedBatch.pass_count ?? 0;
        if (g === 'WARN') return selectedBatch.warn_count ?? 0;
        if (g === 'FAIL') return selectedBatch.fail_count ?? 0;
        return selectedBatch.total_count ?? 0;
    };

    const handleRun = () => {
        if (!runForm.batch_name) { toast.error('평가 이름을 입력해주세요.'); return; }
        if (!runForm.engine_version) { toast.error('AI 엔진 버전을 입력해주세요. 시스템 상태 탭에서 버전을 확인하거나 직접 입력하세요.'); return; }
        const payload = {
            batch_name: runForm.batch_name,
            engine_version: runForm.engine_version,
            llm_model: runForm.llm_model || undefined,
            run_mode: runForm.run_mode,
            created_by: runForm.created_by,
            ...(runForm.run_mode === 'SELECTED' && runForm.qa_ids
                ? { qa_ids: runForm.qa_ids.split(',').map((s) => Number(s.trim())).filter(Boolean) }
                : {}),
            ...(runForm.run_mode === 'CATEGORY' && runForm.category
                ? { category: runForm.category }
                : {}),
        };
        runMutation.mutate(payload, {
            onSuccess: (res) => {
                setShowRunModal(false);
                setRunForm({ ...INIT_RUN });
                // 외부 API 응답: { ok, data: { batch_id, status, total_count } }
                const batchId = res?.data?.data?.batch_id ?? res?.data?.batch_id;
                if (batchId) setSelectedBatchId(String(batchId));
            },
        });
    };

    return (
        <div className="space-y-4 w-full">
            {/* 헤더 */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div>
                    <p className="text-lg font-bold text-sb-header">평가 실행 및 결과</p>
                    <p className="text-base text-gray-400">등록된 질문으로 AI를 테스트합니다. 결과는 <span className="text-emerald-600 font-medium">정상</span> / <span className="text-amber-500 font-medium">주의</span> / <span className="text-red-500 font-medium">개선 필요</span>로 표시됩니다.</p>
                </div>
                <button
                    onClick={() => {
                        setRunForm({
                            ...INIT_RUN,
                            batch_name: makeDefaultBatchName(),
                            created_by: auth?.userId ?? '',
                            engine_version: healthData?.engine_version ?? '',
                        });
                        setShowRunModal(true);
                    }}
                    className="flex items-center gap-1.5 px-4 py-2 text-base font-medium rounded-lg transition-colors shrink-0 text-white bg-sb-primary hover:bg-blue-700"
                >
                    <Play size={14} /> 평가 실행
                </button>
            </div>

            {/* 평가 실행 기록 선택 */}
            <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                <p className="text-base font-semibold text-sb-header mb-1">평가 실행 기록</p>
                <p className="text-sm text-gray-400 mb-3">평가를 실행할 때마다 기록이 쌓입니다. 아래에서 조회할 기록을 선택하세요.</p>
                {bLoading
                    ? <p className="text-base text-gray-400">불러오는 중...</p>
                    : (
                        <div className="space-y-2">
                            <div className="flex flex-wrap gap-2">
                                <button
                                    onClick={() => { setSelectedBatchId(''); setPage(1); }}
                                    className={`px-3 py-1.5 rounded-lg text-base font-medium transition-colors ${!selectedBatchId ? 'bg-sb-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                >
                                    전체
                                </button>
                                {visibleBatches.map((b) => (
                                    <button
                                        key={b.id}
                                        onClick={() => { setSelectedBatchId(String(b.id)); setPage(1); }}
                                        className={`px-3 py-1.5 rounded-lg text-base font-medium transition-colors ${String(selectedBatchId) === String(b.id) ? 'bg-sb-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                    >
                                        {b.batch_name}
                                        <span className={`ml-1.5 text-sm ${String(selectedBatchId) === String(b.id) ? 'text-blue-200' : 'text-gray-400'}`}>
                                            {BATCH_STATUS_LABEL[b.status] ?? b.status}
                                        </span>
                                    </button>
                                ))}
                            </div>
                            {batches.length > MAX_VISIBLE_BATCHES && (
                                <button
                                    onClick={() => setShowAllBatches((v) => !v)}
                                    className="flex items-center gap-1 text-sm text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <ChevronDown size={13} className={`transition-transform ${showAllBatches ? 'rotate-180' : ''}`} />
                                    {showAllBatches ? '접기' : `이전 기록 ${batches.length - MAX_VISIBLE_BATCHES}건 더 보기`}
                                </button>
                            )}
                        </div>
                    )
                }

                {/* 선택된 배치 요약 */}
                {selectedBatch && (
                    <div className="mt-3 pt-3 border-t border-gray-100 space-y-3">
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                            {[
                                { label: '전체', value: selectedBatch.total_count },
                                { label: '정상',     value: selectedBatch.pass_count, color: 'text-emerald-600' },
                                { label: '주의',     value: selectedBatch.warn_count, color: 'text-amber-500' },
                                { label: '개선 필요', value: selectedBatch.fail_count, color: 'text-red-500' },
                                { label: '평균 점수', value: `${selectedBatch.avg_score}점`, color: getScoreColor(selectedBatch.avg_score) },
                            ].map((s, i) => (
                                <div key={i}>
                                    <p className="text-sm text-gray-400">{s.label}</p>
                                    <p className={`text-lg font-bold ${s.color ?? 'text-sb-header'}`}>{s.value}</p>
                                </div>
                            ))}
                        </div>
                        {((selectedBatch.fail_count ?? 0) + (selectedBatch.warn_count ?? 0)) > 0 && (
                            <div className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-amber-50 border border-amber-200">
                                <span className="text-sm text-amber-700">
                                    <strong>{(selectedBatch.fail_count ?? 0) + (selectedBatch.warn_count ?? 0)}건</strong>의 검토가 필요합니다
                                    {(selectedBatch.fail_count ?? 0) > 0 && <span className="ml-2 text-red-600">개선 필요 {selectedBatch.fail_count}건</span>}
                                    {(selectedBatch.warn_count ?? 0) > 0 && <span className="ml-2 text-amber-600">주의 {selectedBatch.warn_count}건</span>}
                                </span>
                                <button
                                    onClick={() => navigate('/ai/admin/console/review')}
                                    className="flex items-center gap-1.5 text-sm text-white bg-amber-500 hover:bg-amber-600 px-3 py-1.5 rounded-lg transition-colors shrink-0"
                                >
                                    결과 검토하러 가기 <ArrowRight size={12} />
                                </button>
                            </div>
                        )}
                        {((selectedBatch.fail_count ?? 0) + (selectedBatch.warn_count ?? 0)) === 0 && selectedBatch.total_count > 0 && (
                            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-100">
                                <span className="text-sm text-emerald-700 font-medium">모든 항목이 PASS입니다</span>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* 판정 기준 안내 */}
            <div className="flex flex-wrap items-center gap-4 px-3 py-2 rounded-lg bg-gray-50 border border-gray-100 text-sm text-gray-500">
                <span className="font-medium text-gray-600">판정 기준</span>
                <span className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500" />정상 — 80점 이상</span>
                <span className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-full bg-amber-400" />주의 — 60~79점</span>
                <span className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-full bg-red-500" />개선 필요 — 60점 미만</span>
                <span className="text-xs text-gray-400 ml-auto">※ 기준 점수는 표준 QA 등록 시 항목별로 조정 가능합니다</span>
            </div>

            {/* 판정 필터 */}
            {/* 필터 */}
            <div className="flex gap-2 flex-wrap items-center">
                {[
                    { key: 'all',  label: '전체' },
                    { key: 'PASS', label: '정상' },
                    { key: 'WARN', label: '주의' },
                    { key: 'FAIL', label: '개선 필요' },
                ].map(({ key, label }) => {
                    const cnt = key !== 'all' ? gradeCount(key) : null;
                    return (
                        <button
                            key={key}
                            onClick={() => { setFilterGrade(key); setPage(1); }}
                            className={`px-3 py-1.5 rounded-lg text-base font-medium transition-colors ${filterGrade === key ? 'bg-sb-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                        >
                            {label}{cnt !== null && ` (${cnt})`}
                        </button>
                    );
                })}
                <select
                    value={filterCategory}
                    onChange={(e) => { setFilterCategory(e.target.value); setPage(1); }}
                    className="px-3 py-1.5 rounded-lg text-base border border-gray-200 bg-white outline-none focus:border-sb-primary"
                >
                    <option value="">전체 카테고리</option>
                    {CONSOLE_CATEGORIES.map((c) => (
                        <option key={c.key} value={c.key}>{c.label}</option>
                    ))}
                </select>
                <input
                    value={filterKeyword}
                    onChange={(e) => { setFilterKeyword(e.target.value); setPage(1); }}
                    placeholder="키워드 검색"
                    className="px-3 py-1.5 rounded-lg text-base border border-gray-200 bg-white outline-none focus:border-sb-primary w-36"
                />
            </div>

            {/* 결과 목록 */}
            <div className="space-y-2">
                {rLoading && <p className="text-center py-8 text-base text-gray-400">불러오는 중...</p>}
                {!rLoading && results.map((ev) => {
                    const VIcon = CONSOLE_VERDICT_STYLE[ev.grade]?.icon;
                    const isOpen = selectedId === ev.id;
                    return (
                        <div key={ev.id} className="rounded-xl border border-gray-100 bg-white shadow-sm hover:shadow-md transition-all overflow-hidden">
                            <div className="p-4 cursor-pointer" onClick={() => setSelectedId(isOpen ? null : ev.id)}>
                                <div className="flex items-center gap-3 group">
                                    {VIcon && <VIcon size={18} className={getVText(ev.grade)} />}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-0.5">
                                            <span className="text-sm text-gray-400">{ev.latency_ms}ms</span>
                                            {ev.core_rule_article && (
                                                <span className="text-sm px-1.5 py-0.5 rounded bg-blue-50 text-blue-600">{ev.core_rule_article}</span>
                                            )}
                                        </div>
                                        <p className="text-base text-sb-header">{ev.question}</p>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <p className={`text-xl font-bold ${getScoreColor(ev.final_score)}`}>
                                            {ev.final_score}<span className="text-base font-normal text-gray-400">점</span>
                                        </p>
                                        <div className="flex items-center gap-1 justify-end mt-0.5">
                                            <span className="text-xs text-gray-400">규칙 기반 {ev.rule_score} / AI 판단 {ev.llm_score}</span>
                                        </div>
                                        <span className={`text-sm px-2 py-0.5 rounded-full font-medium ${getVBg(ev.grade)} ${getVText(ev.grade)}`}>
                                            {ev.grade}{(ev.forbidden_hits_json ?? []).length > 0 ? ' (금지어)' : ''}
                                        </span>
                                    </div>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setDebugId(ev.id); }}
                                        title="기술 상세 정보 (개발자용)"
                                        className="p-1.5 rounded-lg text-gray-200 hover:text-purple-400 hover:bg-purple-50 transition-colors shrink-0 opacity-0 group-hover:opacity-100"
                                    >
                                        <Bug size={14} />
                                    </button>
                                    <ChevronDown size={14} className={`text-gray-400 transition-transform shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
                                </div>
                            </div>

                            {isOpen && (
                                <div className="px-4 pb-4 space-y-3 border-t border-gray-100 pt-3">
                                    {detailLoading && <p className="text-base text-gray-400 text-center py-2">불러오는 중...</p>}
                                    {selectedDetail && (
                                        <>
                                            <div>
                                                <p className="text-sm uppercase tracking-wider text-gray-400 mb-1">기준 답변</p>
                                                <p className="text-base text-gray-500 p-2 rounded-lg bg-blue-50 leading-relaxed">{selectedDetail.standard_answer}</p>
                                            </div>
                                            <div>
                                                <p className="text-sm uppercase tracking-wider text-gray-400 mb-1">AI 답변</p>
                                                <p className="text-base text-gray-700 p-3 rounded-lg leading-relaxed bg-gray-50">{selectedDetail.answer_text}</p>
                                            </div>
                                            <div className="grid grid-cols-3 gap-2 text-center">
                                                {[
                                                    { label: '규칙 기반 점수', value: selectedDetail.rule_score },
                                                    { label: 'AI 판단 점수', value: selectedDetail.llm_score },
                                                    { label: '응답시간', value: `${selectedDetail.latency_ms}ms` },
                                                ].map((s, i) => (
                                                    <div key={i} className="bg-gray-50 rounded-lg py-2">
                                                        <p className="text-sm text-gray-400">{s.label}</p>
                                                        <p className="text-base font-semibold text-sb-header">{s.value}</p>
                                                    </div>
                                                ))}
                                            </div>
                                            {selectedDetail.llm_eval_reason && (
                                                <div>
                                                    <p className="text-sm uppercase tracking-wider text-gray-400 mb-1">AI 판단 이유</p>
                                                    <p className="text-base text-gray-600 italic">{selectedDetail.llm_eval_reason}</p>
                                                </div>
                                            )}
                                            <div className="grid grid-cols-2 gap-3">
                                                <div>
                                                    <p className="text-sm uppercase tracking-wider text-gray-400 mb-1">인용 요소 ({(selectedDetail.citations_json ?? []).length})</p>
                                                    <div className="flex flex-wrap gap-1">
                                                        {(selectedDetail.citations_json ?? []).length > 0
                                                            ? (selectedDetail.citations_json ?? []).map((m, i) => (
                                                                <span key={i} title={typeof m === 'object' ? (m.snippet ?? '') : ''} className="text-sm px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 cursor-default">
                                                                    {typeof m === 'object' ? (m.source || m.article || m.title || JSON.stringify(m)) : m}
                                                                </span>
                                                            ))
                                                            : <span className="text-sm text-gray-400">없음</span>
                                                        }
                                                    </div>
                                                </div>
                                                <div>
                                                    <p className="text-sm uppercase tracking-wider text-gray-400 mb-1">누락 키워드 ({(selectedDetail.missing_terms_json ?? []).length})</p>
                                                    <div className="flex flex-wrap gap-1">
                                                        {(selectedDetail.missing_terms_json ?? []).length > 0
                                                            ? (selectedDetail.missing_terms_json ?? []).map((m, i) => (
                                                                <span key={i} className="text-sm px-1.5 py-0.5 rounded bg-red-100 text-red-700">{m}</span>
                                                            ))
                                                            : <span className="text-sm text-gray-400">없음</span>
                                                        }
                                                    </div>
                                                </div>
                                            </div>
                                            {(selectedDetail.forbidden_hits_json ?? []).length > 0 && (
                                                <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                                                    <p className="text-sm uppercase tracking-wider text-red-500 mb-1 font-bold">금지 키워드 발견</p>
                                                    {(selectedDetail.forbidden_hits_json ?? []).map((f, i) => (
                                                        <p key={i} className="text-base text-red-700">"{f.text}" — {f.reason}</p>
                                                    ))}
                                                </div>
                                            )}
                                            {[
                                                { label: '상세 조문', value: selectedDetail.detail_rule_article },
                                                { label: '제재 조문', value: selectedDetail.sanction_rule_article },
                                                { label: '예외 조문', value: selectedDetail.exception_rule_article },
                                            ].filter((r) => r.value).map((r, i) => (
                                                <div key={i} className="flex items-center gap-2">
                                                    <span className="text-sm text-gray-400">{r.label}:</span>
                                                    <span className="text-sm px-1.5 py-0.5 rounded bg-blue-50 text-blue-600">{r.value}</span>
                                                </div>
                                            ))}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
                {!rLoading && results.length === 0 && (
                    <div className="text-center py-12 text-base text-gray-400">
                        <BarChart2 size={32} className="mx-auto mb-2 text-gray-300" />
                        아직 평가 결과가 없습니다.<br />
                        <span className="text-sm mt-1 block">① 표준 QA가 없다면 먼저 <strong>표준 QA 등록</strong> 탭에서 질문을 등록하세요.</span>
                        <span className="text-sm block">② 등록 후 위의 <strong>평가 실행</strong> 버튼을 눌러 AI를 테스트하세요.</span>
                    </div>
                )}
            </div>

            <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />

            {/* 디버그 상세 모달 */}
            <Modal show={!!debugId} centered onHide={() => setDebugId(null)} size="lg" contentClassName="rounded-2xl border-0 shadow-xl">
                <Modal.Header className="border-b border-gray-100 px-6 py-3">
                    <Modal.Title className="text-base font-bold text-sb-header flex items-center gap-2">
                        <Bug size={14} className="text-purple-500" />
                        평가 결과 디버그 상세
                        {debugData && <span className="text-sm text-gray-400 font-normal">ID: {debugData.id}</span>}
                    </Modal.Title>
                    <button type="button" className="modal-btn-close" onClick={() => setDebugId(null)}>
                        <span className="modal-btn-span">X</span>
                    </button>
                </Modal.Header>
                <Modal.Body className="px-6 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
                    {debugLoading && <p className="text-center py-8 text-base text-gray-400">불러오는 중...</p>}
                    {debugData && (
                        <>
                            {/* 쿼리 파싱 결과 */}
                            {debugData.parsed_query_json && (
                                <div>
                                    <p className="text-sm uppercase tracking-wider text-gray-400 mb-2">쿼리 파싱 결과</p>
                                    <div className="grid grid-cols-2 gap-2">
                                        {Object.entries(debugData.parsed_query_json).map(([k, v]) => (
                                            <div key={k} className="bg-gray-50 rounded-lg p-2.5">
                                                <p className="text-sm text-gray-400 mb-0.5">{k}</p>
                                                <p className="text-base text-sb-header font-mono">
                                                    {Array.isArray(v) ? (v.join(', ') || '-') : (v ?? '-')}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* 검색 후보 문서 */}
                            {(debugData.ranked_candidates_json ?? []).length > 0 && (
                                <div>
                                    <p className="text-sm uppercase tracking-wider text-gray-400 mb-2">
                                        검색 후보 문서 ({debugData.ranked_candidates_json.length})
                                    </p>
                                    <div className="space-y-2">
                                        {debugData.ranked_candidates_json.map((c, i) => (
                                            <div key={i} className="p-3 rounded-lg border border-gray-200 bg-white">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="text-sm font-mono text-gray-400">#{i + 1}</span>
                                                    {c.article_id && (
                                                        <span className="text-sm px-1.5 py-0.5 rounded bg-blue-50 text-blue-600">{c.article_id}</span>
                                                    )}
                                                    {c.score != null && (
                                                        <span className="text-sm text-gray-400 ml-auto">
                                                            score: {typeof c.score === 'number' ? c.score.toFixed(4) : c.score}
                                                        </span>
                                                    )}
                                                </div>
                                                {c.content && (
                                                    <p className="text-xs text-gray-600 leading-relaxed line-clamp-3">{c.content}</p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Slot 선택 결과 */}
                            {debugData.selected_slots_json && (
                                <div>
                                    <p className="text-sm uppercase tracking-wider text-gray-400 mb-2">Slot 선택 결과</p>
                                    <pre className="text-sm font-mono bg-gray-900 text-green-400 p-3 rounded-lg overflow-x-auto leading-relaxed whitespace-pre-wrap">
                                        {JSON.stringify(debugData.selected_slots_json, null, 2)}
                                    </pre>
                                </div>
                            )}

                            {/* 점수 계산 상세 */}
                            {debugData.scoring_breakdown_json && (
                                <div>
                                    <p className="text-sm uppercase tracking-wider text-gray-400 mb-2">점수 계산 상세</p>
                                    <pre className="text-sm font-mono bg-gray-900 text-green-400 p-3 rounded-lg overflow-x-auto leading-relaxed whitespace-pre-wrap">
                                        {JSON.stringify(debugData.scoring_breakdown_json, null, 2)}
                                    </pre>
                                </div>
                            )}

                            {/* LLM Context */}
                            {debugData.raw_context_text && (
                                <div>
                                    <p className="text-sm uppercase tracking-wider text-gray-400 mb-1">LLM Context (raw)</p>
                                    <pre className="text-sm font-mono bg-gray-50 border border-gray-200 p-3 rounded-lg overflow-x-auto leading-relaxed whitespace-pre-wrap max-h-48 overflow-y-auto">
                                        {debugData.raw_context_text}
                                    </pre>
                                </div>
                            )}

                            {/* 원본 답변 */}
                            {debugData.raw_answer_text && (
                                <div>
                                    <p className="text-sm uppercase tracking-wider text-gray-400 mb-1">원본 답변 (raw)</p>
                                    <p className="text-base text-gray-700 p-3 rounded-lg bg-gray-50 border border-gray-200 leading-relaxed whitespace-pre-wrap">
                                        {debugData.raw_answer_text}
                                    </p>
                                </div>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0 px-6 pb-4 pt-0">
                    <Button variant="light" className="text-gray-600" onClick={() => setDebugId(null)}>닫기</Button>
                </Modal.Footer>
            </Modal>

            {/* 평가 실행 모달 */}
            <Modal show={showRunModal} centered onHide={() => setShowRunModal(false)} contentClassName="rounded-2xl border-0 shadow-xl">
                <Modal.Header className="border-b border-gray-100 px-6 py-3">
                    <Modal.Title className="text-base font-bold text-sb-header">평가 실행</Modal.Title>
                    <button type="button" className="modal-btn-close" onClick={() => setShowRunModal(false)}>
                        <span className="modal-btn-span">X</span>
                    </button>
                </Modal.Header>
                <Modal.Body className="px-6 py-4 space-y-3">
                    <div>
                        <label className="text-sm uppercase tracking-wider text-gray-400 block mb-1">평가 이름 *</label>
                        <input
                            value={runForm.batch_name}
                            onChange={(e) => setRunForm({ ...runForm, batch_name: e.target.value })}
                            placeholder="예: 2026-05-19 평가"
                            className="w-full px-3 py-2 rounded-lg text-base border border-gray-200 bg-white outline-none focus:border-sb-primary"
                        />
                    </div>
                    <div>
                        <label className="text-sm uppercase tracking-wider text-gray-400 block mb-1">실행자</label>
                        <span className="flex items-center px-3 py-2 rounded-lg text-base border border-gray-100 bg-gray-50 text-gray-600">
                            {runForm.created_by || '로그인 필요'}
                        </span>
                    </div>
                    <div>
                        <label className="text-sm uppercase tracking-wider text-gray-400 block mb-1">AI 엔진 버전 *</label>
                        <input
                            value={runForm.engine_version}
                            onChange={(e) => setRunForm({ ...runForm, engine_version: e.target.value })}
                            placeholder="예: v1.0 (시스템 상태에서 자동 감지)"
                            className="w-full px-3 py-2 rounded-lg text-base border border-gray-200 bg-white outline-none focus:border-sb-primary font-mono"
                        />
                        <p className="text-xs text-gray-400 mt-1">
                            {healthData?.engine_version
                                ? `현재 감지된 버전: ${healthData.engine_version}`
                                : '엔진 버전을 감지하지 못했습니다. 직접 입력하세요.'}
                        </p>
                    </div>
                    <div>
                        <label className="text-sm uppercase tracking-wider text-gray-400 block mb-1">AI 모델 <span className="text-xs normal-case font-normal text-gray-300">(선택사항 — 비워두면 기본 모델 사용)</span></label>
                        <input
                            value={runForm.llm_model}
                            onChange={(e) => setRunForm({ ...runForm, llm_model: e.target.value })}
                            placeholder="예: gpt-5-mini"
                            className="w-full px-3 py-2 rounded-lg text-base border border-gray-200 bg-white outline-none focus:border-sb-primary"
                        />
                    </div>
                    <div>
                        <label className="text-sm uppercase tracking-wider text-gray-400 block mb-1">실행 범위</label>
                        <select
                            value={runForm.run_mode}
                            onChange={(e) => setRunForm({ ...runForm, run_mode: e.target.value, qa_ids: '', category: '' })}
                            className="w-full px-3 py-2 rounded-lg text-base border border-gray-200 bg-white outline-none focus:border-sb-primary"
                        >
                            <option value="ALL">전체 질문 평가 (권장)</option>
                            <option value="CATEGORY">카테고리별 평가</option>
                            <option value="SELECTED">특정 질문만 평가</option>
                        </select>
                    </div>
                    {runForm.run_mode === 'SELECTED' && (
                        <div>
                            <label className="text-sm uppercase tracking-wider text-gray-400 block mb-1">질문 번호 (쉼표로 구분) *</label>
                            <input
                                value={runForm.qa_ids}
                                onChange={(e) => setRunForm({ ...runForm, qa_ids: e.target.value })}
                                placeholder="예: 1, 2, 3"
                                className="w-full px-3 py-2 rounded-lg text-base border border-gray-200 bg-white outline-none focus:border-sb-primary font-mono"
                            />
                            <p className="text-xs text-gray-400 mt-1">표준 QA 목록에서 확인할 수 있는 질문 번호를 입력하세요</p>
                        </div>
                    )}
                    {runForm.run_mode === 'CATEGORY' && (
                        <div>
                            <label className="text-sm uppercase tracking-wider text-gray-400 block mb-1">카테고리 *</label>
                            <select
                                value={runForm.category}
                                onChange={(e) => setRunForm({ ...runForm, category: e.target.value })}
                                className="w-full px-3 py-2 rounded-lg text-base border border-gray-200 bg-white outline-none focus:border-sb-primary"
                            >
                                <option value="">카테고리 선택</option>
                                {CONSOLE_CATEGORIES.map((c) => (
                                    <option key={c.key} value={c.key}>{c.label}</option>
                                ))}
                            </select>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0 px-6 pb-4 pt-0">
                    <Button variant="light" className="text-gray-600" onClick={() => setShowRunModal(false)}>취소</Button>
                    <Button variant="primary" className="bg-sb-primary border-0" onClick={handleRun} disabled={runMutation.isLoading}>
                        {runMutation.isLoading ? <><RefreshCw size={13} className="me-1 animate-spin" />실행 중...</> : <><Play size={13} className="me-1" />평가 시작</>}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
